import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api.js';
import Button from '../ui/Button.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card.jsx';
import AdminSecretGate from '../components/admin/AdminSecretGate.jsx';
import FamiliesUsageTable from '../components/admin/FamiliesUsageTable.jsx';

const ADMIN_SECRET_STORAGE_KEY = 'ptcm-admin-secret';

function buildAdminHeaders(secret) {
  return {
    'x-admin-secret': secret
  };
}

function countActiveFamilies(families, days) {
  const now = Date.now();
  const thresholdMs = days * 24 * 60 * 60 * 1000;

  return families.filter((family) => {
    if (!family.lastAnsweredAt) {
      return false;
    }

    return now - new Date(family.lastAnsweredAt).getTime() <= thresholdMs;
  }).length;
}

export default function AdminDashboardPage() {
  const [secret, setSecret] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasBootstrapped, setHasBootstrapped] = useState(false);

  const loadDashboard = useCallback(async (candidateSecret) => {
    const trimmedSecret = String(candidateSecret || '').trim();
    if (!trimmedSecret) {
      setError('Veuillez saisir le secret admin.');
      setHasBootstrapped(true);
      return false;
    }

    setIsLoading(true);
    setError('');

    try {
      const payload = await apiFetch('/api/admin/families', {
        headers: buildAdminHeaders(trimmedSecret)
      });

      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(ADMIN_SECRET_STORAGE_KEY, trimmedSecret);
      }

      setSecret(trimmedSecret);
      setDashboard(payload);
      return true;
    } catch (requestError) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(ADMIN_SECRET_STORAGE_KEY);
      }

      setDashboard(null);
      setError(requestError?.message || 'Impossible de charger le backoffice.');
      return false;
    } finally {
      setIsLoading(false);
      setHasBootstrapped(true);
    }
  }, []);

  const clearSecret = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(ADMIN_SECRET_STORAGE_KEY);
    }

    setSecret('');
    setDashboard(null);
    setError('');
    setHasBootstrapped(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setHasBootstrapped(true);
      return;
    }

    const storedSecret = window.sessionStorage.getItem(ADMIN_SECRET_STORAGE_KEY);
    if (!storedSecret) {
      setHasBootstrapped(true);
      return;
    }

    loadDashboard(storedSecret);
  }, [loadDashboard]);

  const families = useMemo(() => dashboard?.families || [], [dashboard]);
  const meta = useMemo(() => dashboard?.meta || {}, [dashboard]);
  const activeFamilies7d = useMemo(() => countActiveFamilies(families, 7), [families]);

  if (!hasBootstrapped && isLoading) {
    return (
      <div className="flex items-center justify-center h-full px-4">
        <div className="text-sm text-ptcm-muted dark:text-gray-400">Chargement du backoffice...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
      {!dashboard ? (
        <AdminSecretGate
          initialSecret={secret}
          isSubmitting={isLoading}
          error={error}
          onSubmit={loadDashboard}
        />
      ) : (
        <>
          <Card>
            <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-2">
                <div className="ptcm-section-pill w-fit">
                  Backoffice
                </div>
                <div className="space-y-1">
                  <CardTitle className="text-2xl">Suivi des inscriptions et usages</CardTitle>
                  <p className="text-sm text-ptcm-muted dark:text-gray-400">
                    Vue foyer avec les membres, leur niveau de completion et la derniere activite questionnaire.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => loadDashboard(secret)} disabled={isLoading}>
                  {isLoading ? 'Rafraichissement...' : 'Rafraichir'}
                </Button>
                <Button variant="ghost" onClick={clearSecret}>
                  Verrouiller
                </Button>
              </div>
            </CardHeader>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="space-y-1">
                <div className="text-sm text-ptcm-muted dark:text-gray-400">Familles</div>
                <div className="text-3xl font-semibold text-ptcm-ink dark:text-gray-100">
                  {meta.totalFamilies || 0}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-1">
                <div className="text-sm text-ptcm-muted dark:text-gray-400">Membres</div>
                <div className="text-3xl font-semibold text-ptcm-ink dark:text-gray-100">
                  {meta.totalMembers || 0}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-1">
                <div className="text-sm text-ptcm-muted dark:text-gray-400">Familles actives sur 7 jours</div>
                <div className="text-3xl font-semibold text-ptcm-ink dark:text-gray-100">
                  {activeFamilies7d}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent>
              <FamiliesUsageTable families={families} />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
